import {
  Alert,
  Button,
  Tab,
  Table,
  TableCell,
  TablePager,
  TableRow,
} from '@fukuri-admins/ui/common';
import { StressAssessmentRole } from '~/types/model/stressAssessment';
import { RemindModal } from '../features/RemindModal';
import { Filter } from '../types';
import styles from './styles.module.scss';

type TabItem = {
  value: string;
  label: string;
};

type User = {
  id: string;
  name: string;
  branchName: string;
  isHighStress: boolean;
  isRowClickable: boolean;
};

type Props = {
  role?: StressAssessmentRole;
  selectedTabValue: Filter;
  tabItems: TabItem[];
  users: User[];
  pagerProps: React.ComponentProps<typeof TablePager>;
  isOpenRemindModal: boolean;
  threeLetterCode: string;
  stressAssessmentId: number;
  showInterviewGuidanceInquiryAlert: boolean;
  inquiryFormUrl: string;
  onFilterChange: (filter: Filter) => void;
  onOpenRemindModal: () => void;
  onCloseRemindModal: () => void;
  onRowClick?: (userId: string) => void;
};

export const StressAssessmentUserReportListPresentation: React.FC<Props> = ({
  role = 'practitioner',
  selectedTabValue,
  users,
  tabItems,
  pagerProps,
  isOpenRemindModal,
  threeLetterCode,
  stressAssessmentId,
  showInterviewGuidanceInquiryAlert,
  inquiryFormUrl,
  onFilterChange,
  onOpenRemindModal,
  onCloseRemindModal,
  onRowClick,
}) => {
  const headers = [
    { label: 'No', className: styles.colNo },
    ...(role === 'implementer'
      ? [{ label: 'ストレス判定', className: styles.colStressLevel }]
      : []),
    { label: '名前' },
    { label: '事業所' },
  ];

  return (
    <div className={styles.container}>
      <Tab items={tabItems} defaultSelectedValue={selectedTabValue} onClick={onFilterChange} />
      {selectedTabValue === 'pending' && (
        <div className={styles.remindButtonWrapper}>
          <Button variant='outline' onClick={onOpenRemindModal}>
            未回答者に再送信
          </Button>
        </div>
      )}
      {selectedTabValue === 'interview_requested' && showInterviewGuidanceInquiryAlert && (
        <div className={styles.inquiryAlertWrapper}>
          <Alert
            status='info'
            title='面談指導の実施には、実施者専用サイトが必要です'
            description='面談指導を希望する社員の個人結果は、実施者（産業医・保健師）専用サイトでのみ閲覧できます。専用サイトのURLはお問い合わせいただいた管理者さまに個別にお渡ししています。'
            action='URLを問い合わせる'
            actionLink={inquiryFormUrl}
          />
        </div>
      )}
      <div className={styles.tableWrapper}>
        <Table headers={headers} fixedHeader maxHeight='384px'>
          {users.map(({ id, name, branchName, isHighStress, isRowClickable }) => (
            <TableRow
              key={id}
              onClick={isRowClickable && onRowClick ? () => onRowClick(id) : undefined}
            >
              <TableCell>{id}</TableCell>
              {role === 'implementer' && (
                <TableCell align='center'>
                  {isHighStress && (
                    <div className={styles.stressLevelBadge}>
                      <span className={styles.label}>高</span>
                    </div>
                  )}
                </TableCell>
              )}
              <TableCell>{name}</TableCell>
              <TableCell>{branchName}</TableCell>
            </TableRow>
          ))}
        </Table>
      </div>
      <TablePager {...pagerProps} />
      {isOpenRemindModal && (
        <RemindModal
          threeLetterCode={threeLetterCode}
          stressAssessmentId={stressAssessmentId}
          onClose={onCloseRemindModal}
        />
      )}
    </div>
  );
};
